import React, { useState, useEffect, useRef } from 'react';
import { Check, X, Phone, Clock, User, Bell, ChevronDown, ChevronUp, RefreshCw, LogOut, Search, Ticket, ShoppingCart, ArrowLeft, MessageSquare } from 'lucide-react';
import { EmptyState, NoActiveOrdersIllustration } from './EmptyStates.jsx';
import { RestaurantLogo } from './Logo.jsx';
import BRAND_COLORS, { COLOR_USAGE, GRADIENTS } from './brandColors.js';
import { auth, isFirebaseConfigured } from './firebase.js';
import { RecaptchaVerifier, signInWithPhoneNumber, signOut } from 'firebase/auth';
import * as api from './lib/api.js';
import { useViewTransition } from './useViewTransition.js';
import { ConfirmModal, useConfirm } from './ConfirmModal.jsx';
import { OTPInput } from './OTPInput.jsx';

// Tab order used to compute slide direction on tab switches.
const TAB_ORDER = ['active', 'completed', 'walkin'];

// System / auth / walk-in error messages.
// TODO(i18n) — AWAITING TRANSLATION (Katie): English-only placeholders shown in all
// five languages until the Master doc supplies es/zh/vi/ar copy. Per the
// translation-ownership rule we do NOT invent the non-English copy; the merge loop
// below seeds this English set into every language block, overridden as translated
// keys land. See the pending list compiled for Katie.
const PENDING_TRANSLATION_ERRORS = {
  errInvalidNumber: "Invalid number. Please try again.",
  errVerifyCouldNotStart: "Verification could not start. Please try again.",
  errSmsNotConfigured: "SMS service is not configured. Please contact support.",
  errSendCodeFailed: "Failed to send verification code. Please try again.",
  errEnter6Digit: "Please enter a 6-digit code",
  errAuthUnavailable: "Authentication service is not available. Please contact support.",
  errTooManyAttempts: "Too many attempts. Please wait a few minutes and try again.",
  errSmsQuota: "SMS limit reached for now. Please try again later or contact SF New Deal.",
  errSendCodeGeneric: "Could not send the code. Please check the number and try again.",
  errNotRegistered: "This phone number is not registered to a restaurant. Please contact SF New Deal if you believe this is an error.",
  errVerifyRegistration: "Unable to verify restaurant registration. Please try again or contact support.",
  errInvalidCode: "Invalid verification code. Please try again.",
  errVerificationFailed: "Verification failed. Please try again.",
  errRestaurantNotLoaded: "Restaurant information not loaded",
  errMarkPickedUpFailed: "Could not mark the order as picked up. Please try again.",
  errEnterFamilyCode: "Please enter a family code.",
  errFamilyCodeNotFound: "Family code not found. Please recheck the code with the family.",
  errVerifyCodeFailed: "Error verifying code. Please try again.",
  errEnter4Digits: "Please enter exactly 4 digits.",
  errPhoneMismatch: "Phone number doesn't match. Please reconfirm the last 4 digits with the family.",
  errVerifyPhoneFailed: "Error verifying phone. Please try again.",
  errSubmitOrderFailed: "Could not submit order. Please try again or call the SFND call center at (415) 480-1185.",
};

// Restaurant-dashboard translations. Mirrors the family app's translations.js
// for shared strings; values come from the Master: Finalized Text / Translations
// for Interface doc (archived at /translations.pdf at repo root).
const RESTAURANT_TRANSLATIONS = {
  en: {
    programName: "Family Meal Pack Program",
    restaurantDashboard: "Restaurant Dashboard",
    pleaseLogin: "Please log in to view and manage your orders",
    phoneNumber: "Phone number",
    phoneHint: "Enter the phone number registered with your restaurant",
    sendCode: "Send verification code",
    verificationCode: "Verification code",
    verify: "Verify & login",
    changePhone: "← Change phone number",
    logout: "Logout",
    logoutConfirm: "Are you sure you want to log out?",
    cancel: "Cancel",
    activeOrders: "Active orders",
    completedOrders: "Previous orders",
    walkInOrder: "Walk-in order",
    todaysOrders: "Orders today",
    active: "Active",
    today: "Today",
    previous: "Previous",
    completed: "Completed",
    cancelled: "Canceled",
    markPickedUp: "Mark as picked up",
    cancelOrder: "Cancel order",
    noActiveOrders: "No active orders",
    noCompletedOrders: "No previous orders",
    viewActiveOrders: "View active orders",
    waitingForPickup: "Waiting for pickup",
    troubleLoggingIn: "Having trouble logging in?",
    needSupport: "Please contact the SF New Deal call center at",
    extensions: "Dial extension #2 for Cantonese, #3 for Mandarin, #4 for Spanish.",
    needHelp: "Need help?",
    walkInCallCenter: "Call the SFND call center at",
    walkInCallCenterSuffix: "for assistance with walk-in or redemption orders.",
    familyCodeLabel: "Family Code",
    contactLabel: "Contact",
    itemsOrderedLabel: "Items Ordered",
    dietaryRestrictionsLabel: "Dietary Restrictions",
    languageLabel: "Language",
    mealsLabel: "Meals",
    walkInTitle: "Walk-In / Redemption Card Order",
    walkInStep1: "Step 1: Enter Family Code",
    walkInStep1Hint: "Ask the family for their full family code (e.g., SmiS1234, CheC5678). Spaces are optional.",
    walkInStep1Placeholder: "Enter full family code",
    verifyFamilyCodeBtn: "Verify Family Code",
    verifyingShort: "Verifying...",
    walkInStep2: "Step 2: Verify Phone Number",
    walkInStep2Hint: "Ask the family for the last 4 digits of their phone number",
    walkInLast4Placeholder: "Last 4 digits",
    verifyPhoneBtn: "Verify Phone Number",
    selectMenuItemsTitle: "Select Menu Items",
    addToOrderBtn: "Add to Order",
    notEnoughMealsBtn: "Not Enough Meals",
    placeOrderBtn: "Place Order",
    startOver: "Start Over",
    emptyTodayTitle: "No orders yet today",
    emptyTodayMessage: "New orders will appear here automatically and you'll hear a notification sound.",
    emptyPreviousTitle: "No previous orders",
    emptyPreviousMessage: "Orders from the last 7 days will appear here.",
    testNotificationSound: "Test Notification Sound",
    verifyIdentityIntro: "Verify the family's identity with a two-step process.",
    familyCodeVerifiedIntro: "Family code verified. Now verify the phone number.",
    familyCodeVerifiedBadge: "Family Code Verified:",
  },
  es: {
    programName: "El Programa de Comidas Familiares",
    restaurantDashboard: "Panel de control del restaurante",
    pleaseLogin: "Por favor inicie su sesión para ver y gestionar sus pedidos",
    phoneNumber: "Número de teléfono",
    phoneHint: "Ingrese el número de teléfono registrado con su restaurante",
    sendCode: "Enviar código de verificación",
    verificationCode: "Código de verificación",
    verify: "Verificar e iniciar sesión",
    changePhone: "← Cambiar número de teléfono",
    logout: "Cerrar Sesión",
    logoutConfirm: "¿Está seguro de que desea cerrar sesión?",
    cancel: "Cancelar",
    activeOrders: "Pedidos activos",
    completedOrders: "Pedidos anteriores",
    walkInOrder: "Pedido en el local",
    todaysOrders: "Pedidos de hoy",
    active: "Activos",
    today: "Hoy",
    previous: "Anteriores",
    completed: "Completado",
    cancelled: "Cancelado",
    markPickedUp: "Marcar como recogido",
    cancelOrder: "Cancelar pedido",
    noActiveOrders: "No hay pedidos activos",
    noCompletedOrders: "No hay pedidos anteriores",
    viewActiveOrders: "Ver pedidos activos",
    waitingForPickup: "En espera",
    troubleLoggingIn: "¿Tiene problemas para iniciar sesión?",
    needSupport: "Comuníquese con el centro de llamada de SF New Deal al",
    extensions: "Extensión #2 para cantonés, #3 para mandarín, #4 para español.",
    needHelp: "¿Necesita ayuda?",
    walkInCallCenter: "Llame al centro de llamadas de SFND al",
    walkInCallCenterSuffix: "para obtener ayuda con pedidos en persona o con tarjeta de canje.",
    familyCodeLabel: "Identificación familiar",
    contactLabel: "Contacto",
    itemsOrderedLabel: "Artículos pedidos",
    dietaryRestrictionsLabel: "Restricciones dietéticas",
    languageLabel: "Idioma",
    mealsLabel: "Comidas",
    walkInTitle: "Pedido en el local / Tarjeta de canje",
    walkInStep1: "Paso 1: Ingrese la identificación familiar",
    walkInStep1Hint: "Pida a la familia su código familiar completo (p. ej., SmiS1234, CheC5678). Los espacios son opcionales.",
    walkInStep1Placeholder: "Ingrese el código familiar completo",
    verifyFamilyCodeBtn: "Verificar identificación familiar",
    verifyingShort: "Verificando...",
    walkInStep2: "Paso 2: Verifique el número de teléfono",
    walkInStep2Hint: "Pida a la familia los últimos 4 dígitos de su número de teléfono",
    walkInLast4Placeholder: "Últimos 4 dígitos",
    verifyPhoneBtn: "Verificar número de teléfono",
    selectMenuItemsTitle: "Seleccionar artículos del menú",
    addToOrderBtn: "Agregar al pedido",
    notEnoughMealsBtn: "Comidas insuficientes",
    placeOrderBtn: "Realizar pedido",
    startOver: "Empezar de nuevo",
    emptyTodayTitle: "No hay pedidos hoy",
    emptyTodayMessage: "Los nuevos pedidos aparecerán aquí automáticamente y escuchará un sonido de notificación.",
    emptyPreviousTitle: "No hay pedidos anteriores",
    emptyPreviousMessage: "Los pedidos de los últimos 7 días aparecerán aquí.",
    testNotificationSound: "Probar sonido de notificación",
    verifyIdentityIntro: "Verifique la identidad de la familia mediante un proceso de dos pasos.",
    familyCodeVerifiedIntro: "Identificación familiar verificada. Ahora verifique el número de teléfono.",
    familyCodeVerifiedBadge: "Identificación familiar verificada:",
  },
  zh: {
    programName: "家庭餐活動",
    restaurantDashboard: "餐廳公告版/點餐介面",
    pleaseLogin: "請登入查詢並管理您的訂單",
    phoneNumber: "電話號碼",
    phoneHint: "輸入您餐廳註冊使用的電話號碼",
    sendCode: "發送驗證碼",
    verificationCode: "驗證碼",
    verify: "驗證 & 登錄",
    changePhone: "← 更換電話號碼",
    logout: "登出",
    logoutConfirm: "您確定要登出嗎？",
    cancel: "取消",
    activeOrders: "已下訂單",
    completedOrders: "歷史訂單",
    walkInOrder: "現場點餐",
    todaysOrders: "今天的訂單",
    active: "已下訂單",
    today: "今天",
    previous: "之前",
    completed: "已完成",
    cancelled: "已取消",
    markPickedUp: "標記為已取餐",
    cancelOrder: "取消訂單",
    noActiveOrders: "沒有有效訂單",
    noCompletedOrders: "暫無歷史訂單",
    viewActiveOrders: "查看有效訂單",
    waitingForPickup: "等待中",
    troubleLoggingIn: "無法登錄？",
    needSupport: "請聯繫我們的呼叫中心電話",
    extensions: "選擇分機號2為粵語，3為普通話。",
    needHelp: "需要幫助？",
    walkInCallCenter: "撥打SFND呼叫中心電話",
    walkInCallCenterSuffix: "獲取現場點餐或免費餐卡訂單協助。",
    familyCodeLabel: "家庭餐ID",
    contactLabel: "聯絡方式",
    itemsOrderedLabel: "已下單",
    dietaryRestrictionsLabel: "飲食禁忌",
    languageLabel: "語言",
    mealsLabel: "餐點",
    walkInTitle: "現場點餐 / 免費餐卡訂單",
    walkInStep1: "第一步：輸入家庭ID",
    walkInStep1Hint: "請活動參加者提供完整的家庭餐ID（例如 SmiS1234, CheC5678）。空格可省略。",
    walkInStep1Placeholder: "輸入完整家庭餐ID",
    verifyFamilyCodeBtn: "驗證家庭餐ID",
    verifyingShort: "驗證中...",
    walkInStep2: "第二步：驗證電話號碼",
    walkInStep2Hint: "詢問活動參加者最後4位電話號碼",
    walkInLast4Placeholder: "後4位電話號碼",
    verifyPhoneBtn: "驗證電話號碼",
    selectMenuItemsTitle: "選擇菜單選項",
    addToOrderBtn: "加入訂單",
    notEnoughMealsBtn: "餐點不足",
    placeOrderBtn: "訂餐",
    startOver: "重新開始",
    emptyTodayTitle: "今天暫無訂單",
    emptyTodayMessage: "新訂單會自動顯示在這裡，並伴隨通知聲。",
    emptyPreviousTitle: "暫無歷史訂單",
    emptyPreviousMessage: "過去7天內的訂單會顯示在這裡。",
    testNotificationSound: "測試通知聲",
    verifyIdentityIntro: "通過雙重驗證確認家庭身份。",
    familyCodeVerifiedIntro: "家庭ID已核實。請繼續驗證電話號碼。",
    familyCodeVerifiedBadge: "家庭ID已核實：",
  },
  ar: {
    programName: "برنامج الوجبات العائلية",
    restaurantDashboard: "لوحة تحكم المطعم",
    pleaseLogin: "يرجى تسجيل الدخول لعرض طلباتك وإدارتها",
    phoneNumber: "رقم الهاتف",
    phoneHint: "أدخل رقم الهاتف المسجل لدى مطعمك",
    sendCode: "إرسال رمز التحقق",
    verificationCode: "رمز التحقق",
    verify: "التحقق وتسجيل الدخول",
    changePhone: "→ تغيير رقم الهاتف",
    logout: "تسجيل الخروج",
    logoutConfirm: "هل أنت متأكد أنك تريد تسجيل الخروج؟",
    cancel: "إلغاء",
    activeOrders: "الطلبات النشطة",
    completedOrders: "الطلبات السابقة",
    walkInOrder: "طلب حضوري",
    todaysOrders: "طلبات اليوم",
    active: "نشطة",
    today: "اليوم",
    previous: "السابقة",
    completed: "مكتمل",
    cancelled: "ملغى",
    markPickedUp: "تحديد كـ تم الاستلام",
    cancelOrder: "إلغاء الطلب",
    noActiveOrders: "لا توجد طلبات نشطة",
    noCompletedOrders: "لا توجد طلبات سابقة",
    viewActiveOrders: "عرض الطلبات النشطة",
    waitingForPickup: "في الانتظار",
    troubleLoggingIn: "تواجه مشكلة في تسجيل الدخول؟",
    needSupport: "يرجى الاتصال بمركز اتصال SF New Deal على",
    extensions: "اضغط #2 للكانتونية، #3 للماندرين، #4 للإسبانية.",
    needHelp: "تحتاج مساعدة؟",
    walkInCallCenter: "اتصل بمركز اتصال SFND على",
    walkInCallCenterSuffix: "للمساعدة في الطلبات الحضورية أو طلبات بطاقة الاسترداد.",
    familyCodeLabel: "معرف العائلة",
    contactLabel: "جهة الاتصال",
    itemsOrderedLabel: "الأصناف المطلوبة",
    dietaryRestrictionsLabel: "قيود غذائية",
    languageLabel: "اللغة",
    mealsLabel: "وجبات",
    walkInTitle: "طلب حضوري / بطاقة استرداد",
    walkInStep1: "الخطوة 1: أدخل معرف العائلة",
    walkInStep1Hint: "اطلب من العائلة رمز عائلتها الكامل (مثلاً SMI S1234، CHE C5678). المسافات اختيارية.",
    walkInStep1Placeholder: "أدخل رمز العائلة كاملاً",
    verifyFamilyCodeBtn: "تحقق من معرف العائلة",
    verifyingShort: "جاري التحقق...",
    walkInStep2: "الخطوة 2: تحقق من رقم الهاتف",
    walkInStep2Hint: "اطلب من العائلة آخر 4 أرقام من رقم هاتفها",
    walkInLast4Placeholder: "آخر 4 أرقام",
    verifyPhoneBtn: "تحقق من رقم الهاتف",
    selectMenuItemsTitle: "حدد أصناف قائمة الطعام",
    addToOrderBtn: "الإضافة إلى الطلب",
    notEnoughMealsBtn: "الوجبات غير كافية",
    placeOrderBtn: "إتمام الطلب",
    startOver: "ابدأ من جديد",
    emptyTodayTitle: "لا توجد طلبات اليوم بعد",
    emptyTodayMessage: "ستظهر الطلبات الجديدة هنا تلقائيًا وستسمع نغمة إشعار.",
    emptyPreviousTitle: "لا توجد طلبات سابقة",
    emptyPreviousMessage: "ستظهر هنا طلبات آخر 7 أيام.",
    testNotificationSound: "اختبار نغمة الإشعار",
    verifyIdentityIntro: "تحقق من هوية العائلة من خلال عملية من خطوتين.",
    familyCodeVerifiedIntro: "تم التحقق من معرف العائلة. تحقق الآن من رقم الهاتف.",
    familyCodeVerifiedBadge: "تم التحقق من معرف العائلة:",
  },
  vi: {
    programName: "Family Meal Pack Program",
    restaurantDashboard: "Bảng điều khiển của nhà hàng",
    pleaseLogin: "Vui lòng đăng nhập để xem và quản lý các đơn đặt hàng của quý vị",
    phoneNumber: "Số điện thoại",
    phoneHint: "Nhập số điện thoại đã đăng ký với nhà hàng của quý vị",
    sendCode: "Gửi mã xác thực",
    verificationCode: "Mã xác thực",
    verify: "Xác thực & đăng nhập",
    changePhone: "← Thay đổi số điện thoại",
    logout: "Đăng xuất",
    logoutConfirm: "Quý vị có chắc chắn muốn đăng xuất không?",
    cancel: "Hủy",
    activeOrders: "Các đơn đặt hàng đang hoạt động",
    completedOrders: "Các đơn đặt hàng trước đó",
    walkInOrder: "Đơn hàng trực tiếp tại nhà hàng",
    todaysOrders: "Các đơn đặt hàng hôm nay",
    active: "Đang hoạt động",
    today: "Hôm nay",
    previous: "Trước đây",
    completed: "Đã hoàn thành",
    cancelled: "Đã hủy",
    markPickedUp: "Đánh dấu là đã lấy hàng",
    cancelOrder: "Hủy đơn đặt hàng",
    noActiveOrders: "Không có đơn hàng đang hoạt động",
    noCompletedOrders: "Không có đơn đặt hàng trước đó",
    viewActiveOrders: "Xem các đơn đặt hàng đang hoạt động",
    waitingForPickup: "Đang chờ lấy hàng",
    troubleLoggingIn: "Gặp sự cố khi đăng nhập?",
    needSupport: "Vui lòng gọi tổng đài SF New Deal theo số",
    extensions: "Bấm phím #2 cho tiếng Quảng, #3 cho tiếng Quan thoại, #4 cho tiếng Tây Ban Nha.",
    needHelp: "Cần trợ giúp?",
    walkInCallCenter: "Gọi tổng đài SFND theo số",
    walkInCallCenterSuffix: "để được hỗ trợ với đơn hàng trực tiếp hoặc thẻ quy đổi.",
    familyCodeLabel: "Mã định danh gia đình",
    contactLabel: "Liên hệ",
    itemsOrderedLabel: "Các món đã đặt",
    dietaryRestrictionsLabel: "Hạn chế về chế độ ăn uống",
    languageLabel: "Ngôn ngữ",
    mealsLabel: "Bữa ăn",
    walkInTitle: "Đơn hàng trực tiếp / Thẻ quy đổi",
    walkInStep1: "Bước 1: Nhập mã định danh gia đình",
    walkInStep1Hint: "Yêu cầu gia đình cung cấp mã định danh đầy đủ (ví dụ SmiS1234, CheC5678). Có thể bỏ qua khoảng trắng.",
    walkInStep1Placeholder: "Nhập mã định danh gia đình đầy đủ",
    verifyFamilyCodeBtn: "Xác thực mã định danh gia đình",
    verifyingShort: "Đang xác thực...",
    walkInStep2: "Bước 2: Xác thực số điện thoại",
    walkInStep2Hint: "Yêu cầu gia đình cung cấp 4 chữ số cuối của số điện thoại",
    walkInLast4Placeholder: "4 chữ số cuối",
    verifyPhoneBtn: "Xác thực số điện thoại",
    selectMenuItemsTitle: "Chọn các món trong thực đơn",
    addToOrderBtn: "Thêm vào đơn đặt hàng",
    notEnoughMealsBtn: "Không đủ bữa ăn",
    placeOrderBtn: "Đặt hàng",
    startOver: "Bắt đầu lại",
    emptyTodayTitle: "Chưa có đơn đặt hàng nào hôm nay",
    emptyTodayMessage: "Các đơn đặt hàng mới sẽ xuất hiện ở đây tự động và quý vị sẽ nghe thấy âm thanh thông báo.",
    emptyPreviousTitle: "Không có đơn đặt hàng trước đó",
    emptyPreviousMessage: "Các đơn đặt hàng trong 7 ngày qua sẽ xuất hiện ở đây.",
    testNotificationSound: "Kiểm tra âm thanh thông báo",
    verifyIdentityIntro: "Xác thực danh tính của gia đình bằng quy trình hai bước.",
    familyCodeVerifiedIntro: "Mã định danh gia đình đã được xác thực. Tiếp tục xác thực số điện thoại.",
    familyCodeVerifiedBadge: "Mã định danh gia đình đã được xác thực:",
  }
};

// Seed every language with the English error fallbacks (PENDING_TRANSLATION_ERRORS),
// then let each block's own keys override — so translated copy takes precedence
// the moment it lands, with no caller changes.
for (const lang of Object.keys(RESTAURANT_TRANSLATIONS)) {
  RESTAURANT_TRANSLATIONS[lang] = { ...PENDING_TRANSLATION_ERRORS, ...RESTAURANT_TRANSLATIONS[lang] };
}

export default function RestaurantDashboard() {
  const [language, setLanguage] = useState('en');
  const t = RESTAURANT_TRANSLATIONS[language] || RESTAURANT_TRANSLATIONS.en;

  const { view: selectedTab, navigateTo: navigateToTab, direction: tabDirection } = useViewTransition('active');
  const { confirm, confirmProps } = useConfirm();
  // setSelectedTab shim: derive slide direction from tab order so a left-to-right
  // tab tap reads as "forward" and right-to-left reads as "back".
  const setSelectedTab = (next) => {
    const fromIdx = TAB_ORDER.indexOf(selectedTab);
    const toIdx = TAB_ORDER.indexOf(next);
    const dir = toIdx > fromIdx ? 'forward' : 'back';
    navigateToTab(next, dir);
  };
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const previousOrderCount = useRef(0);
  // Holds the latest fetchOrders closure so the 15s poll always calls the current
  // version (with the resolved restaurant + demoMode), never a stale one captured
  // when the interval was first created.
  const fetchOrdersRef = useRef(null);

  // Walk-in order state - Two-step verification system
  const [walkInView, setWalkInView] = useState('code-entry'); // 'code-entry', 'phone-verification', 'family-verified', 'order-placed'
  const [familyCodeInput, setFamilyCodeInput] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [walkInCart, setWalkInCart] = useState([]);
  const [verificationError, setVerificationError] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [justAddedItem, setJustAddedItem] = useState(false); // For animation feedback
  const [walkInCartExpanded, setWalkInCartExpanded] = useState(false);
  const [submittingWalkIn, setSubmittingWalkIn] = useState(false); // In-flight guard against double-tap submit

  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true); // Start as true to check for existing session
  const [sendingSmsCode, setSendingSmsCode] = useState(false);
  const [verifyingSmsCode, setVerifyingSmsCode] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [authError, setAuthError] = useState('');
  const [authStep, setAuthStep] = useState('phone'); // 'phone' or 'verify'
  
  // Airtable access now goes through the `/api/*` proxy (see src/lib/api.js).
  // No token or base id in the client — the Lambda holds the Airtable PAT and
  // scopes every request to the Firebase-authenticated caller.

  // Heuristic filter for dietary restriction strings until the Airtable schema returns a
  // single language per call. Categorizes each string by dominant Unicode script and keeps
  // only entries matching the restaurant's selected language. English/Spanish both fall
  // under Latin and collide — acceptable until the master translation pass replaces this.
  const filterDietaryByLanguage = (values, lang) => {
    if (!Array.isArray(values)) return [];
    const detect = (s) => {
      if (!s || typeof s !== 'string') return 'unknown';
      if (/[一-鿿]/.test(s)) return 'zh';
      if (/[؀-ۿ]/.test(s)) return 'ar';
      return 'latin';
    };
    return values.filter(v => {
      const d = detect(v);
      if (lang === 'zh') return d === 'zh';
      if (lang === 'ar') return d === 'ar';
      return d === 'latin';
    });
  };
  
  // Demo restaurant with menu
  const [restaurant, setRestaurant] = useState({
    id: 'recDemoRestaurant123',
    dbaName: "Golden Dragon",
    phone: "+14155551234",
    todayDate: new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
    menu: [
      {
        id: 'item1',
        nameEN: 'Orange Chicken Family Pack',
        descriptionEN: 'Crispy chicken in sweet orange sauce with steamed rice and vegetables. Serves 4-5 people.',
        imageUrl: 'https://images.unsplash.com/photo-1596040033229-a0b2a9199083?w=400',
        vouchers: 1
      },
      {
        id: 'item2',
        nameEN: 'Kung Pao Chicken Family Pack',
        descriptionEN: 'Spicy chicken with peanuts, vegetables, and rice. Serves 4-5 people.',
        imageUrl: 'https://images.unsplash.com/photo-1569718212165-3a8278d5f624?w=400',
        vouchers: 1
      },
      {
        id: 'item3',
        nameEN: 'Vegetable Chow Mein Family Pack',
        descriptionEN: 'Stir-fried noodles with fresh vegetables. Serves 4-5 people.',
        imageUrl: 'https://images.unsplash.com/photo-1555126634-323283e090fa?w=400',
        vouchers: 1
      }
    ]
  });

  // Check for existing session on mount.
  // Note: reCAPTCHA is created lazily inside sendVerificationCode rather than at mount.
  // Mount-time setup is fragile because the recaptcha-container DOM node may not exist
  // yet (authLoading=true on first render) and Firebase verifiers also break if the
  // underlying DOM node gets re-rendered before .verify() runs.
  useEffect(() => {
    checkAuthState();
  }, []);

  // Sync <html dir> and <html lang> with the active language so modals,
  // portals, and detached UI inherit the right text direction. Arabic
  // flips to RTL; everything else stays LTR.
  useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const checkAuthState = () => {
    // Check Firebase auth state if configured
    if (isFirebaseConfigured() && auth) {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
          console.log('[Auth] Restaurant user signed in:', user.phoneNumber);
          setIsAuthenticated(true);
          // Load restaurant profile
          loadRestaurantProfile(user);
        } else {
          console.log('[Auth] No restaurant user signed in');
          setIsAuthenticated(false);
        }
        setAuthLoading(false);
      });
      return unsubscribe;
    } else {
      // Fallback to localStorage check for demo mode
      const savedAuth = localStorage.getItem('restaurantAuth');
      const savedRestaurant = localStorage.getItem('restaurantProfile');

      if (savedAuth === 'true' && savedRestaurant) {
        setIsAuthenticated(true);
        const parsedRestaurant = JSON.parse(savedRestaurant);
        // Ensure menu exists in the loaded restaurant
        if (!parsedRestaurant.menu || parsedRestaurant.menu.length === 0) {
          parsedRestaurant.menu = [
            {
              id: 'item1',
              nameEN: 'Orange Chicken Family Pack',
              descriptionEN: 'Crispy chicken in sweet orange sauce with steamed rice and vegetables. Serves 4-5 people.',
              imageUrl: 'https://images.unsplash.com/photo-1596040033229-a0b2a9199083?w=400',
              vouchers: 1
            },
            {
              id: 'item2',
              nameEN: 'Kung Pao Chicken Family Pack',
              descriptionEN: 'Spicy chicken with peanuts, vegetables, and rice. Serves 4-5 people.',
              imageUrl: 'https://images.unsplash.com/photo-1569718212165-3a8278d5f624?w=400',
              vouchers: 1
            },
            {
              id: 'item3',
              nameEN: 'Vegetable Chow Mein Family Pack',
              descriptionEN: 'Stir-fried noodles with fresh vegetables. Serves 4-5 people.',
              imageUrl: 'https://images.unsplash.com/photo-1555126634-323283e090fa?w=400',
              vouchers: 1
            }
          ];
        }
        setRestaurant(parsedRestaurant);
        loadDemoData();
      }
      setAuthLoading(false);
    }
  };

  const loadRestaurantProfile = async (user) => {
    try {
      console.log('[Auth] Looking up restaurant profile for phone:', user.phoneNumber);

      // Resolve the caller's Restaurants record through the proxy. The server
      // matches on the verified token phone (SMS + SMS Second Contact, all
      // formats) and returns the record, or 403 NOT_REGISTERED when there's none.
      let restaurant;
      try {
        restaurant = await api.getRestaurantProfile();
      } catch (e) {
        if (e.code === 'NOT_REGISTERED' || e.status === 403) restaurant = null;
        else throw e;
      }

      if (restaurant) {
        const restaurantRecord = restaurant.fields;
        console.log('[Auth] Found restaurant profile:', restaurantRecord['DBA Name']);

        // Build menu from Airtable fields. Photos come from the `Menu Item #N Photo`
        // attachment field; prefer the Airtable-served 512px thumbnail for speed.
        const pickPhoto = (att) => {
          if (!Array.isArray(att) || att.length === 0) return null;
          const a = att[0];
          return a.thumbnails?.large?.url || a.thumbnails?.small?.url || a.url || null;
        };
        // Restaurants can offer up to 6 menu items.
        const menu = [];
        for (let i = 1; i <= 6; i++) {
          const menuItemEN = restaurantRecord[`Menu Item #${i} English`] || restaurantRecord[`Menu Item #${i}`];
          if (menuItemEN) {
            menu.push({
              id: `item${i}`,
              nameEN: menuItemEN,
              nameES: restaurantRecord[`Menu Item #${i} Spanish`] || menuItemEN,
              nameZH: restaurantRecord[`Menu Item #${i} Chinese`] || menuItemEN,
              nameAR: restaurantRecord[`Menu Item #${i} Arabic`] || menuItemEN,
              imageUrl: pickPhoto(restaurantRecord[`Menu Item #${i} Photo`]),
              dietaryConsiderations: restaurantRecord[`Menu Item #${i} Dietary Considerations`] || [],
              vouchers: 1
            });
          }
        }

        const dbaName = restaurantRecord['DBA Name'] || "Restaurant";
        // Test restaurants (marked with Test=true in Airtable) see all orders including staff orders
        // Matt's Bistro is hardcoded as a test restaurant for Alpha 2 belt-and-suspenders;
        // Airtable's `Test` field remains the source of truth for any other test restaurant.
        const isTestRestaurant =
          restaurantRecord['Test'] === true ||
          (restaurantRecord['DBA Name'] || '').toLowerCase().includes("matt's bistro");

        const profile = {
          id: restaurant.id,
          dbaName: dbaName,
          phone: user.phoneNumber,
          address: restaurantRecord['Street Address'] || '',
          neighborhood: restaurantRecord['Neighborhoods'] || '',
          cuisineType: restaurantRecord['Cuisine Type'] || '',
          availableForOrders: restaurantRecord['Available For Orders'] === '✅',
          openTime: restaurantRecord['Open'] || '',
          closeTime: restaurantRecord['Close'] || '',
          logo: pickPhoto(restaurantRecord['Restaurant Logo']),
          todayDate: new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
          menu: menu.length > 0 ? menu : restaurant.menu, // Fallback to demo menu if no menu items
          isTestRestaurant: isTestRestaurant // Test restaurants see all orders including staff
        };

        setRestaurant(profile);
        localStorage.setItem('restaurantProfile', JSON.stringify(profile));
        setDemoMode(false); // Switch to live mode since we found the restaurant
        // Orders are loaded by the useEffect on restaurant.dbaName once state updates.
      } else {
        // Phone number not registered to any restaurant - block access
        console.warn('[Auth] No restaurant profile found for phone:', user.phoneNumber);
        setAuthError(t.errNotRegistered);
        // Sign out the user since they're not authorized
        if (auth) {
          await signOut(auth);
        }
        setIsAuthenticated(false);
        setAuthStep('phone');
        localStorage.removeItem('restaurantAuth');
      }
    } catch (error) {
      console.error('[Auth] Error loading restaurant profile:', error);
      // On error, block access and show error message
      setAuthError(t.errVerifyRegistration);
      if (auth) {
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.error('[Auth] Error signing out:', signOutError);
        }
      }
      setIsAuthenticated(false);
      setAuthStep('phone');
      localStorage.removeItem('restaurantAuth');
    }
  };

  // Note: a previous loadOrdersFromAirtable function used to live here but queried a
  // non-existent `Orders` table (the real table is `Order Log`) and on failure fell
  // back to loadDemoData(), which flipped demoMode=true and replaced real orders with
  // demo data for the rest of the session. The initial-load path now goes through the
  // single fetchOrders flow below, triggered by a useEffect on restaurant.dbaName.

  // Format phone number for display
  const formatPhoneForDisplay = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // Validate + normalize a US phone number to E.164 (+1XXXXXXXXXX). Returns null when
  // the number isn't a valid US number — e.g. wrong digit count, or an area code that
  // starts with 0/1 (which is what happens when a stray "1" gets prefixed and pushes
  // off the last digit). Catching this client-side keeps a bad number from ever
  // touching reCAPTCHA, which is what left the widget stuck before.
  const toE164US = (raw) => {
    const d = (raw || '').replace(/\D/g, '');
    let ten;
    if (d.length === 10) ten = d;
    else if (d.length === 11 && d[0] === '1') ten = d.slice(1);
    else return null;
    if (ten[0] === '0' || ten[0] === '1') return null; // invalid US area code
    return `+1${ten}`;
  };

  // Fully tear down any rendered reCAPTCHA so the next attempt starts clean. The
  // Firebase verifier is single-use; after a failed send it (and the widget it
  // injected into the container) would otherwise persist, causing every retry to
  // fail until the app was reloaded.
  const resetRecaptcha = () => {
    try { if (recaptchaVerifier) recaptchaVerifier.clear(); } catch (e) { /* ignore */ }
    setRecaptchaVerifier(null);
    const el = document.getElementById('recaptcha-container');
    if (el) el.innerHTML = '';
  };

  // Map Firebase auth error codes to friendly, non-technical messages.
  const friendlySmsError = (err) => {
    const code = (err && (err.code || err.message)) || '';
    if (code.includes('invalid-phone-number')) return t.errInvalidNumber;
    if (code.includes('too-many-requests')) return t.errTooManyAttempts;
    if (code.includes('captcha') || code.includes('app-credential')) return t.errVerifyCouldNotStart;
    if (code.includes('quota')) return t.errSmsQuota;
    return t.errSendCodeGeneric;
  };

  // Send SMS verification code.
  // Uses sendingSmsCode (not authLoading) so the top-level loading-screen branch doesn't
  // unmount the login screen mid-send and tear down the recaptcha-container DOM node.
  const sendVerificationCode = async (e) => {
    if (e) e.preventDefault();
    setSendingSmsCode(true);
    setAuthError('');

    try {
      // Validate the number BEFORE touching reCAPTCHA so a bad number can't corrupt it.
      const formattedPhone = toE164US(phoneNumber);
      if (!formattedPhone) {
        setAuthError(t.errInvalidNumber);
        setSendingSmsCode(false);
        return;
      }

      console.log('[Auth] Sending verification code to:', formattedPhone);

      if (isFirebaseConfigured() && auth) {
        // Start from a clean slate every time — clears any prior (single-use) verifier
        // and the widget it left in the container.
        resetRecaptcha();
        let verifier;
        try {
          verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible',
            callback: () => console.log('[Auth] reCAPTCHA solved'),
            'expired-callback': () => console.log('[Auth] reCAPTCHA expired')
          });
          setRecaptchaVerifier(verifier);
        } catch (err) {
          console.error('[Auth] reCAPTCHA init error:', err);
          resetRecaptcha();
          setAuthError(t.errVerifyCouldNotStart);
          setSendingSmsCode(false);
          return;
        }
        try {
          const result = await signInWithPhoneNumber(auth, formattedPhone, verifier);
          setConfirmationResult(result);
          setAuthStep('verify');
          console.log('[Auth] Verification code sent successfully');
        } catch (firebaseError) {
          console.error('[Auth] Firebase SMS error:', firebaseError);
          // Reset so the user can immediately retry without reloading the app.
          resetRecaptcha();
          setAuthError(friendlySmsError(firebaseError));
        }
      } else {
        if (import.meta.env.PROD) {
          console.error('[Auth] Firebase is not configured in this production build.');
          setAuthError(t.errSmsNotConfigured);
          setSendingSmsCode(false);
          return;
        }
        // Dev-only demo SMS fallback
        console.log('[Auth] Using demo mode (Firebase not configured) — DEV ONLY');
        setAuthError('⚠️ Demo Mode: Firebase not configured. Use code 123456 to continue.');
        setTimeout(() => setAuthStep('verify'), 1000);
      }
    } catch (error) {
      console.error('[Auth] Send code error:', error);
      setAuthError(t.errSendCodeFailed);
    } finally {
      setSendingSmsCode(false);
    }
  };

  // Verify SMS code.
  // Uses verifyingCode (not authLoading) for the same reason as sendVerificationCode —
  // keeps the verify screen mounted while Firebase confirms the code.
  const verifyCode = async (e) => {
    if (e) e.preventDefault();
    setVerifyingSmsCode(true);
    setAuthError('');

    try {
      console.log('[Auth] Verifying code...');

      if (isFirebaseConfigured() && confirmationResult) {
        try {
          const result = await confirmationResult.confirm(verificationCode);
          console.log('[Auth] User signed in successfully:', result.user.phoneNumber);
          localStorage.setItem('restaurantAuth', 'true');
          // Firebase onAuthStateChanged will handle setting isAuthenticated
        } catch (firebaseError) {
          console.error('[Auth] Firebase verification error:', firebaseError);
          setAuthError(t.errInvalidCode);
        }
      } else {
        if (import.meta.env.PROD) {
          console.error('[Auth] Firebase is not configured in this production build.');
          setAuthError(t.errAuthUnavailable);
          setVerifyingSmsCode(false);
          return;
        }
        // Dev-only demo verify fallback
        if (verificationCode.length === 6) {
          setIsAuthenticated(true);
          localStorage.setItem('restaurantAuth', 'true');
          localStorage.setItem('restaurantProfile', JSON.stringify(restaurant));
          loadDemoData();
        } else {
          setAuthError(t.errEnter6Digit);
        }
      }
    } catch (error) {
      console.error('[Auth] Verify code error:', error);
      setAuthError(t.errVerificationFailed);
    } finally {
      setVerifyingSmsCode(false);
    }
  };

  // Logout
  const handleLogout = async () => {
    // Confirm before logging out so staff/restaurants don't accidentally tap it.
    const ok = await confirm({
      title: t.logout,
      message: t.logoutConfirm,
      confirmLabel: t.logout,
      cancelLabel: t.cancel,
      variant: 'danger',
    });
    if (!ok) return;
    if (isFirebaseConfigured() && auth) {
      try {
        await signOut(auth);
        console.log('[Auth] Restaurant user signed out');
      } catch (error) {
        console.error('[Auth] Sign out error:', error);
      }
    }

    // Reset reCAPTCHA verifier for next login
    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear();
      } catch (e) {
        console.log('[Auth] Could not clear recaptcha on logout:', e);
      }
      setRecaptchaVerifier(null);
    }

    setIsAuthenticated(false);
    setPhoneNumber('');
    setVerificationCode('');
    setConfirmationResult(null);
    setAuthStep('phone');
    setOrders([]);

    // Recreate reCAPTCHA verifier after a short delay
    setTimeout(() => {
      if (isFirebaseConfigured() && auth) {
        try {
          const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible',
            callback: () => console.log('[Auth] reCAPTCHA solved'),
            'expired-callback': () => console.log('[Auth] reCAPTCHA expired')
          });
          setRecaptchaVerifier(verifier);
          console.log('[Auth] reCAPTCHA recreated after logout');
        } catch (e) {
          console.error('[Auth] Failed to recreate reCAPTCHA after logout:', e);
        }
      }
    }, 500);

    // Clear persisted auth state
    localStorage.removeItem('restaurantAuth');
    localStorage.removeItem('restaurantProfile');
  };

  // No auto-load on auth: data is loaded explicitly by loadRestaurantProfile (real auth)
  // or the localStorage fallback path. A blanket loadDemoData here was racing the real
  // Airtable fetch and overwriting real orders with demo data.

  // Keep the ref pointed at the freshest fetchOrders every render so the polling
  // interval below never calls a stale closure (which previously queried the
  // default "Golden Dragon" demo restaurant and blanked real orders every tick).
  // Must run in an effect (not as a bare statement) because fetchOrders is a
  // `const` declared further down — referencing it during render would TDZ-crash
  // the dashboard.
  useEffect(() => {
    fetchOrdersRef.current = fetchOrders;
  });

  // Fetch orders immediately as soon as we know which restaurant we are. Without
  // this, the dashboard would sit empty for up to 15 seconds after login while it
  // waited for the polling interval below to tick the first time.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!restaurant?.dbaName) return;
    fetchOrders();
  }, [isAuthenticated, restaurant?.dbaName]);

  // Auto-refresh orders every 15 seconds. Calls through the ref so it always runs
  // the latest fetchOrders (current restaurant + demoMode), not the closure that
  // existed when isAuthenticated first flipped true.
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      fetchOrdersRef.current?.();
    }, 15000); // Poll every 15 seconds

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const loadDemoData = () => {
    const now = new Date();
    const fmt = (d) => d.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
    const makeOrder = (id, minutesAgo, familyCode, totalMeals, menuItems, dietaryRestrictions, language, orderMethod) => {
      const createdAt = new Date(now.getTime() - minutesAgo * 60000);
      return {
        id,
        createdAt,
        timestamp: fmt(createdAt),
        familyCode,
        totalMeals,
        pickedUp: false,
        cancelled: false,
        menuItems,
        dietaryRestrictions,
        language,
        orderMethod
      };
    };

    const demoOrders = [
      // Today
      makeOrder('demo1', 10, 'SMI S1234', 2,
        [{ name: 'Orange Chicken Family Pack', amount: 1 }, { name: 'Kung Pao Chicken Family Pack', amount: 1 }],
        ['No Restrictions'], 'English', 'Mobile App'),
      makeOrder('demo2', 25, 'CHE C5678', 1,
        [{ name: 'Vegetable Chow Mein Family Pack', amount: 1 }],
        ['Vegetarian', 'No Garlic'], 'Chinese', 'Texting'),
      makeOrder('demo3', 45, 'GAR G9012', 3,
        [{ name: 'Orange Chicken Family Pack', amount: 2 }, { name: 'Vegetable Chow Mein Family Pack', amount: 1 }],
        ['No Peanuts'], 'Spanish', 'Mobile App'),
      // Previous (1-7 days ago)
      makeOrder('demo4', 60 * 24, 'JOH J2222', 2,
        [{ name: 'Orange Chicken Family Pack', amount: 2 }],
        ['No Pork'], 'English', 'Redemption Card'),
      makeOrder('demo5', 60 * 24 * 3, 'LEE L3456', 1,
        [{ name: 'Kung Pao Chicken Family Pack', amount: 1 }],
        ['No Restrictions'], 'English', 'Mobile App'),
      makeOrder('demo6', 60 * 24 * 6, 'WAN W7890', 2,
        [{ name: 'Vegetable Chow Mein Family Pack', amount: 2 }],
        ['Vegan'], 'Chinese', 'Texting')
    ];

    setOrders(demoOrders);
    setDemoMode(true);
    setLoading(false);
    setError(null);
  };

  const fetchOrders = async () => {
    if (demoMode) {
      // In demo mode, silently reload data (no loading spinner to avoid flashing)
      loadDemoData();
      return;
    }

    // In production, fetch this restaurant's orders via the API proxy
    try {
      // Guard against calling before the restaurant profile has loaded.
      if (!restaurant?.dbaName) {
        console.error('[Orders] No restaurant name available for filtering');
        setError(t.errRestaurantNotLoaded);
        return;
      }

      // Fetch this restaurant's orders through the proxy. The server scopes to the
      // authenticated restaurant (no client-supplied name), filters out SFND
      // staff/QA orders, and resolves human family codes — so the client just formats.
      const { records: filteredRecords, familyCodeById } = await api.getRestaurantOrders();

      const formattedOrders = filteredRecords.map(record => {
        const fields = record.fields;
        const menuItems = [];

        // Restaurants can offer up to 6 menu items. Resolve the item NAME from the
        // restaurant's loaded menu by position (id item1..item6) so it's language-
        // aware and covers items #5/#6 — the Order Log only has `Menu Item 1`..
        // `Menu Item 4` lookup fields (5 and 6 don't exist), so relying on those
        // alone showed a literal "Menu Item 6" placeholder.
        const nameKey = { es: 'nameES', zh: 'nameZH', ar: 'nameAR' }[language] || 'nameEN';
        for (let i = 1; i <= 6; i++) {
          const amount = fields[`Menu Item #${i} Amount`];
          if (amount > 0) {
            const menuMatch = restaurant?.menu?.find(m => m.id === `item${i}`);
            const name = menuMatch
              ? (menuMatch[nameKey] || menuMatch.nameEN)
              : (fields[`Menu Item ${i}`]?.[0] || `Menu Item ${i}`);
            menuItems.push({ name, amount });
          }
        }

        const createdAt = fields['Timestamp'] ? new Date(fields['Timestamp']) : new Date();
        return {
          id: record.id,
          createdAt,
          timestamp: createdAt.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }),
          familyCode: familyCodeById[fields['Family']?.[0]] || fields['RECORD_ID (from Family)']?.[0] || 'N/A',
          totalMeals: fields['Total Meals'] || 0,
          pickedUp: fields['Picked Up?'] || false,
          cancelled: fields['Cancel Order!'] || false,
          menuItems: menuItems,
          dietaryRestrictions: fields['Dietary Restriction (from Families)'] || [],
          language: fields['Language (from Family) 2']?.[0] || 'English',
          orderMethod: fields['Order Method']?.[0] || 'Texting'
        };
      });

      console.log(`[Orders] Loaded ${formattedOrders.length} orders`);
      setOrders(formattedOrders);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError(`Error loading orders: ${error.message}`);
      setLoading(false);
    }
  };

  const markPickedUp = async (orderId) => {
    if (demoMode) {
      setOrders(orders.map(order =>
        order.id === orderId ? { ...order, pickedUp: true } : order
      ));
      return;
    }
    // Optimistically update, then persist via the proxy (which verifies the order
    // belongs to this restaurant). Revert + surface an error if the write fails so
    // staff never think a pickup saved when it didn't.
    const prev = orders;
    setOrders(orders.map(order =>
      order.id === orderId ? { ...order, pickedUp: true } : order
    ));
    try {
      await api.setOrderStatus(orderId, { pickedUp: true });
    } catch (e) {
      console.error('[Orders] Failed to mark picked up:', e);
      setOrders(prev);
      setError(t.errMarkPickedUpFailed);
    }
  };

  const playDing = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const audioContext = new Ctx();
      // iOS Safari suspends the context until a user gesture resumes it
      const start = () => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      };
      if (audioContext.state === 'suspended' && typeof audioContext.resume === 'function') {
        audioContext.resume().then(start).catch(() => start());
      } else {
        start();
      }
    } catch (err) {
      console.warn('[Audio] Could not play ding:', err);
    }
  };

  // Walk-in order functions
  // Demo family database - In production, this data comes from Airtable
  const getAllFamilies = () => {
    return [
      {
        id: 'recFamily1',
        familyCode: 'SMI S1234',
        phone: '+1 (415) 555-1234',
        secondaryPhone: '+1 (415) 555-4321',
        vouchersRemaining: 3,
        language: 'English',
        dietaryRestrictions: ['No Restrictions']
      },
      {
        id: 'recFamily2',
        familyCode: 'CHE C5678',
        phone: '+1 (415) 555-5678',
        secondaryPhone: '+1 (415) 555-8765',
        vouchersRemaining: 2,
        language: 'Chinese',
        dietaryRestrictions: ['Vegetarian', 'No Garlic']
      },
      {
        id: 'recFamily3',
        familyCode: 'GAR G9012',
        phone: '+1 (415) 555-9012',
        secondaryPhone: '+1 (415) 555-2109',
        vouchersRemaining: 1,
        language: 'Spanish',
        dietaryRestrictions: ['No Peanuts']
      }
    ];
  };

  // Step 1: Verify family code
  const verifyFamilyCode = async () => {
    if (!familyCodeInput.trim()) {
      setVerificationError(t.errEnterFamilyCode);
      return;
    }

    setVerifyingCode(true);
    setVerificationError('');

    if (demoMode) {
      // Demo mode: Check against demo families
      const demoFamilies = getAllFamilies();

      setTimeout(() => {
        // Normalize both codes by removing spaces and converting to uppercase
        const normalizedInput = familyCodeInput.trim().replace(/\s/g, '').toUpperCase();
        const family = demoFamilies.find(f =>
          f.familyCode.replace(/\s/g, '').toUpperCase() === normalizedInput
        );

        if (family) {
          // Code is valid, store family data and move to phone verification
          setSelectedFamily(family);
          setWalkInView('phone-verification');
          setVerificationError('');
        } else {
          // Code is invalid
          setVerificationError(t.errFamilyCodeNotFound);
        }
        setVerifyingCode(false);
      }, 500);
    } else {
      // Production mode: verify the family code via the proxy. The server returns
      // a minimal projection (no phone numbers); phone verification happens
      // server-side in verifyPhone so full numbers never reach the browser.
      try {
        const family = await api.verifyWalkInFamily(familyCodeInput);
        setSelectedFamily(family);
        setWalkInView('phone-verification');
        setVerificationError('');
      } catch (error) {
        if (error.code === 'NOT_FOUND' || error.status === 404) {
          setVerificationError(t.errFamilyCodeNotFound);
        } else {
          console.error('[Walk-In] Error verifying family code:', error);
          setVerificationError(t.errVerifyCodeFailed);
        }
      }
      setVerifyingCode(false);
    }
  };

  // Step 2: Verify phone number (last 4 digits)
  const verifyPhone = async () => {
    if (!phoneInput.trim() || phoneInput.length !== 4) {
      setVerificationError(t.errEnter4Digits);
      return;
    }

    setVerifyingPhone(true);
    setVerificationError('');

    const onMatch = (isMatch) => {
      if (isMatch) {
        setWalkInView('family-verified');
        setVerificationError('');
      } else {
        setVerificationError(t.errPhoneMismatch);
        setPhoneInput(''); // Clear input for retry
      }
    };

    if (demoMode) {
      // Demo families carry full phone numbers locally — check the last 4 here.
      setTimeout(() => {
        const last4Primary = (selectedFamily.phone || '').replace(/\D/g, '').slice(-4);
        const last4Secondary = selectedFamily.secondaryPhone
          ? selectedFamily.secondaryPhone.replace(/\D/g, '').slice(-4)
          : null;
        onMatch(phoneInput === last4Primary || (!!last4Secondary && phoneInput === last4Secondary));
        setVerifyingPhone(false);
      }, 500);
      return;
    }

    // Production: the server holds the phone numbers and verifies the last 4, so
    // full numbers never reach the browser.
    try {
      const { match } = await api.verifyWalkInPhone(selectedFamily.id, phoneInput);
      onMatch(match);
    } catch (error) {
      console.error('[Walk-In] Error verifying phone:', error);
      setVerificationError(t.errVerifyPhoneFailed);
    } finally {
      setVerifyingPhone(false);
    }
  };

  const addToWalkInCart = (item) => {
    const currentVouchersUsed = walkInCart.reduce((sum, cartItem) => sum + cartItem.vouchers, 0);
    const newTotalVouchers = currentVouchersUsed + item.vouchers;

    if (newTotalVouchers <= selectedFamily.vouchersRemaining) {
      setWalkInCart([...walkInCart, item]);

      // Trigger pulse animation
      setJustAddedItem(true);
      setTimeout(() => setJustAddedItem(false), 600); // Animation lasts 600ms

      // Auto-expand cart when vouchers are fully consumed so the family can review/swap
      if (newTotalVouchers === selectedFamily.vouchersRemaining) {
        setWalkInCartExpanded(true);
      }
    }
  };

  const removeFromWalkInCart = (index) => {
    setWalkInCart(walkInCart.filter((_, i) => i !== index));
  };

  const submitWalkInOrder = async () => {
    // Double-tap guard: a slow proxy round-trip leaves the button live long
    // enough to fire a second POST (= a duplicate order + double voucher debit).
    // Bail if a submit is already in flight; cleared in the catch / resetWalkInOrder.
    if (walkInCart.length === 0 || submittingWalkIn) return;
    setSubmittingWalkIn(true);

    if (demoMode) {
      // In demo mode, just show success
      setTimeout(() => {
        setWalkInView('order-placed');
        // Switch to Active Orders tab after 2 seconds
        setTimeout(() => {
          resetWalkInOrder();
          setSelectedTab('active'); // Redirect to Active Orders tab
        }, 2000);
      }, 500);
    } else {
      // In production, submit the walk-in order through the proxy. The server
      // binds the order to the authenticated restaurant, validates the family's
      // voucher balance, writes the Order Log row, and decrements vouchers —
      // mirroring the family app's placeOrder so a walk-in is indistinguishable
      // from a family-initiated order in downstream automations.
      try {
        // Tally per-item quantities. Cart items are individual meals (one cart
        // entry per voucher), summed into "Menu Item #N Amount" by visible menu
        // position (item1..item6 → N=1..6).
        const positionForId = (id) => {
          if (typeof id !== 'string') return null;
          const m = id.match(/^item(\d+)$/);
          if (!m) return null;
          const n = parseInt(m[1], 10);
          return n >= 1 && n <= 6 ? n : null;
        };
        const amounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
        walkInCart.forEach((item) => {
          const pos = positionForId(item.id);
          if (pos) amounts[pos] += 1;
        });

        await api.submitWalkInOrder({ familyId: selectedFamily.id, amounts });

        setWalkInView('order-placed');
        fetchOrders(); // Refresh orders list
        // Switch to Today tab after 2 seconds
        setTimeout(() => {
          resetWalkInOrder();
          setSelectedTab('active'); // 'active' tab is now labeled "Today"
        }, 2000);
      } catch (error) {
        console.error('[Walk-In] Error submitting order:', error);
        const msg = error.code === 'INSUFFICIENT_VOUCHERS'
          ? error.message
          : t.errSubmitOrderFailed;
        setVerificationError(msg);
        setSubmittingWalkIn(false); // Re-enable submit so staff can retry after a failure
      }
    }
  };

  const resetWalkInOrder = () => {
    setWalkInView('code-entry');
    setFamilyCodeInput('');
    setPhoneInput('');
    setSelectedFamily(null);
    setWalkInCart([]);
    setWalkInCartExpanded(false);
    setVerificationError('');
    setSubmittingWalkIn(false);
  };

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const todayOrders = orders.filter(o => o.createdAt && !o.cancelled && isSameDay(o.createdAt, new Date()));
  const previousOrders = (() => {
    const now = new Date();
    const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    cutoff.setDate(cutoff.getDate() - 7);
    return orders
      .filter(o => o.createdAt && !o.cancelled && !isSameDay(o.createdAt, now) && o.createdAt >= cutoff)
      .sort((a, b) => b.createdAt - a.createdAt);
  })();

  useEffect(() => {
    const todayOrderCount = todayOrders.length;
    if (previousOrderCount.current > 0 && todayOrderCount > previousOrderCount.current) {
      playDing();
    }
    previousOrderCount.current = todayOrderCount;
  }, [orders]);

  // Rendered by CALLING OrderCard({ order }) (not <OrderCard/>) — see the map
  // call sites below. OrderCard is defined inside this component, so it has a new
  // function identity every render; used as a <Component/> that forces React to
  // unmount/remount every card on each state change (re-running each card's
  // fade-in animation = the visible flicker on tap / on the 15s poll). Called as
  // a plain function, the returned <div key=…> reconciles in place. Safe because
  // OrderCard has no internal hooks.
  const OrderCard = ({ order }) => {
    const isExpanded = expandedOrder === order.id;

    return (
      <div key={order.id} className="bg-white rounded-card shadow-elevation-1 mb-4 overflow-hidden card-interactive transition-shadow duration-200 hover:shadow-elevation-2">
        <div
          className="p-5 cursor-pointer"
          onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h3 className="font-bold text-body-lg text-gray-900" dir="ltr">
                  {order.familyCode}
                </h3>
                {order.pickedUp && (
                  <span className="inline-flex items-center gap-1 text-label font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-800">
                    <Check size={12} /> {t.completed}
                  </span>
                )}
              </div>
              <div className="text-body-sm text-gray-600 flex items-center gap-1.5">
                <Clock size={14} />
                <span>{order.timestamp}</span>
              </div>
            </div>
            <button className="text-gray-400 hover:text-gray-600 transition-colors">
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>

          <div className="flex items-center gap-4 text-body-sm">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-teal-50 to-cyan-50" style={{ borderLeft: '3px solid BRAND_COLORS.teal' }}>
              <span className="text-2xl">🍽️</span>
              <span className="font-bold text-body-lg" style={{ color: BRAND_COLORS.turquoise }}>
                {order.totalMeals}
              </span>
              <span className="text-body-sm text-gray-600">meal{order.totalMeals > 1 ? 's' : ''}</span>
            </div>
            <div className="text-gray-600 font-medium flex items-center gap-1.5">
              <User size={14} />
              <span>{order.orderMethod}</span>
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-200 p-5 bg-gray-50">
            <div className="mb-4">
              <div className="text-label font-bold text-gray-500 uppercase tracking-wide mb-1">{t.familyCodeLabel}</div>
              <div className="text-gray-900 font-mono text-body-lg font-semibold" dir="ltr">{order.familyCode}</div>
            </div>

            <div className="mb-4">
              <div className="text-label font-bold text-gray-500 uppercase tracking-wide mb-2">{t.itemsOrderedLabel}</div>
              {order.menuItems.length > 0 ? (
                <div className="space-y-1">
                  {order.menuItems.map((item, idx) => (
                    <div key={idx} className="text-gray-900 font-medium">
                      <span className="font-bold" style={{ color: BRAND_COLORS.turquoise }}>{item.amount}x</span> {item.name}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 text-body-sm">No items listed</div>
              )}
            </div>

            {(() => {
              const filtered = filterDietaryByLanguage(order.dietaryRestrictions, language);
              if (!filtered.length) return null;
              return (
                <div className="mb-4">
                  <div className="text-label font-bold text-gray-500 uppercase tracking-wide mb-2">{t.dietaryRestrictionsLabel}</div>
                  <div className="flex flex-wrap gap-2">
                    {filtered.map((restriction, idx) => (
                      <span key={idx} className="bg-yellow-100 text-yellow-800 text-label px-3 py-1.5 rounded-full font-semibold">
                        ⚠️ {restriction}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="mb-5">
              <div className="text-label font-bold text-gray-500 uppercase tracking-wide mb-1">{t.languageLabel}</div>
              <div className="text-gray-900 font-medium">{order.language}</div>
            </div>
          </div>
        )}

        {/* Restaurant-facing cancel intentionally omitted: the program team does
            not want restaurants cancelling meals without staff approval (abuse
            history). Cancellation stays a staff action (Status = "Canceled" in
            Airtable). The full cancel feature + a recommended approval-gated
            design is stashed in memory (restaurant-cancel-feature) for later. */}
        {!order.pickedUp && (
          <div className="border-t border-gray-100 p-4">
            <button
              onClick={() => markPickedUp(order.id)}
              className="w-full min-h-touch rounded-btn font-semibold text-body-md flex items-center justify-center gap-2 active:opacity-80 transition-opacity"
              style={{ backgroundColor: BRAND_COLORS.teal, color: BRAND_COLORS.midnight }}
            >
              <Check size={18} /> {t.markPickedUp}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Main render
  if (authLoading) {
    // Show loading screen while checking for existing session
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        {/* reCAPTCHA container must always be present */}
        <div id="recaptcha-container"></div>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 mx-auto mb-4" style={{ borderColor: BRAND_COLORS.teal }}></div>
          <p className="text-gray-600 text-body-lg font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* reCAPTCHA container - invisible */}
          <div id="recaptcha-container"></div>

          <div className="text-center mb-8">
            <img src="/sfnd-hsa-combined-logo.png" alt="SF New Deal & HSA" className="mx-auto mb-4" style={{ maxWidth: '70%', height: 'auto' }} />
            <h1 className="text-title-lg font-bold text-gray-800 mb-2">{t.programName} {t.restaurantDashboard}</h1>
            <p className="text-gray-600">{t.pleaseLogin}</p>
          </div>

          {authStep === 'phone' ? (
            <div className="bg-white rounded-card shadow-elevation-2 p-6 mb-4">
              <div className="mb-4">
                <label className="block text-body-sm font-semibold text-gray-700 mb-2">
                  {t.phoneNumber}
                </label>
                <p className="text-label text-gray-500 mb-2">
                  {t.phoneHint}
                </p>
                <div className="relative">
                  <div className="absolute inset-y-0 start-0 flex items-center ps-4 pointer-events-none">
                    <span className="text-body-lg text-gray-500">+1</span>
                    <div className="w-px h-5 bg-gray-300 ms-3" />
                  </div>
                  <input
                    type="tel"
                    inputMode="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="(415) 555-1234"
                    autoComplete="tel-national"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    className="w-full h-touch-md rounded-btn border-2 border-gray-200 ps-16 pe-4 text-body-lg text-gray-900 focus:outline-none focus:border-teal transition-colors duration-150 placeholder:text-gray-400"
                  />
                </div>
              </div>

              {authError && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-btn text-body-sm">
                  {authError}
                </div>
              )}

              <button
                type="button"
                onClick={sendVerificationCode}
                disabled={sendingSmsCode || !toE164US(phoneNumber)}
                className="w-full h-touch-md text-midnight rounded-btn font-semibold text-body-lg mb-3 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
                style={{ backgroundColor: BRAND_COLORS.teal }}
              >
                {sendingSmsCode ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {t.sendCode}
                  </span>
                ) : t.sendCode}
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-card shadow-elevation-2 p-6 mb-4">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-body-sm font-semibold text-gray-700">
                    {t.verificationCode}
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthStep('phone');
                      setVerificationCode('');
                      setAuthError('');
                    }}
                    className="text-label hover:underline"
                    style={{ color: BRAND_COLORS.turquoise }}
                  >
                    {t.changePhone}
                  </button>
                </div>
                <p className="text-label text-gray-500 mb-3">
                  Enter the 6-digit code sent to {formatPhoneForDisplay(phoneNumber)}
                </p>
                <OTPInput
                  value={verificationCode}
                  onChange={(v) => setVerificationCode(v.replace(/\D/g, '').slice(0, 6))}
                  autoFocus
                />
              </div>

              {authError && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-btn text-body-sm">
                  {authError}
                </div>
              )}

              <button
                type="button"
                onClick={verifyCode}
                disabled={verifyingSmsCode || verificationCode.length !== 6}
                className="w-full h-touch-md text-midnight rounded-btn font-semibold text-body-lg mb-3 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
                style={{ backgroundColor: BRAND_COLORS.teal }}
              >
                {verifyingSmsCode ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    {t.verify}
                  </span>
                ) : t.verify}
              </button>

              <button
                type="button"
                onClick={sendVerificationCode}
                disabled={sendingSmsCode}
                className="w-full text-gray-600 text-body-sm hover:text-gray-800"
              >
                Didn't receive a code? Send again
              </button>
            </div>
          )}

          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4 text-body-sm text-gray-700">
            <p className="font-semibold mb-1">{t.troubleLoggingIn}</p>
            <p>
              {t.needSupport}{' '}
              <a href="tel:+14154801185" className="font-bold hover:underline" style={{ color: BRAND_COLORS.turquoise }}>
                (415) 480-1185
              </a>
              . {t.extensions}
            </p>
          </div>

          <div className="mt-6 text-center">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="text-gray-600 px-3 min-h-touch rounded border border-gray-300 text-body-sm"
              aria-label="Language"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="zh">中文</option>
              <option value="vi">Tiếng Việt</option>
              <option value="ar">العربية</option>
            </select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-root min-h-screen bg-gray-50">
      {/* Custom CSS for pulse-glow animation */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 10px 25px -5px rgba(42, 172, 173, 0.1), 0 8px 10px -6px rgba(42, 172, 173, 0.1);
          }
          50% {
            transform: scale(1.01);
            box-shadow: 0 20px 45px -5px rgba(42, 172, 173, 0.3), 0 15px 20px -6px rgba(42, 172, 173, 0.25);
          }
        }
        .animate-pulse-glow {
          animation: pulse-glow 0.6s ease-in-out;
        }
      `}</style>
      {/* Header */}
      <div className="bg-white sticky top-0 z-50 shadow-elevation-2">
        {/* Brand bar with gradient */}
        <div className="h-1" style={{
          background: GRADIENTS.brandBar
        }} />

        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            {/* Logo (program name + restaurant name live on the row below) */}
            <div className="flex items-center gap-3 min-w-0">
              <RestaurantLogo size="medium" />
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="text-label font-medium ps-3 pe-7 min-h-touch rounded border focus:outline-none flex-shrink-0"
                style={{ borderColor: '#E5E7EB', color: '#374151' }}
                aria-label="Language"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="zh">中文</option>
                <option value="vi">Tiếng Việt</option>
                <option value="ar">العربية</option>
              </select>
              <button
                onClick={fetchOrders}
                className="w-touch h-touch flex items-center justify-center rounded-lg transition-all hover:bg-gray-100"
                style={{ color: BRAND_COLORS.turquoise }}
                title="Refresh orders"
                aria-label="Refresh orders"
              >
                <RefreshCw size={20} />
              </button>
              <button
                onClick={playDing}
                className="w-touch h-touch flex items-center justify-center rounded-lg transition-all hover:bg-gray-100"
                style={{ color: BRAND_COLORS.goji }}
                title="Test notification sound"
                aria-label="Test notification sound"
              >
                <Bell size={20} />
              </button>
              <button
                onClick={handleLogout}
                className="w-touch h-touch flex items-center justify-center rounded-lg transition-all hover:bg-gray-100"
                style={{ color: '#6B7280' }}
                title={t.logout}
                aria-label={t.logout}
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>

          {/* Program name + restaurant name on their own row for breathing room */}
          <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-3 flex-wrap">
            <span className="text-label font-medium text-gray-500 leading-tight">{t.programName}</span>
            <h1 className="text-body-lg font-bold text-gray-900 leading-tight">{restaurant.dbaName}</h1>
            {demoMode && (
              <span className="text-label font-semibold px-2 py-0.5 rounded" style={{
                backgroundColor: '#FEF3C7',
                color: '#92400E'
              }}>
                DEMO MODE
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start justify-between gap-3">
            <span className="text-red-800 text-body-sm font-medium">{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 flex-shrink-0" aria-label="Dismiss error">
              <X size={18} />
            </button>
          </div>
        )}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="text-body-sm font-semibold text-gray-600 uppercase tracking-wide mb-1">{restaurant.todayDate}</div>
          <div className="flex items-baseline gap-3">
            <div className="text-display font-bold" style={{ color: BRAND_COLORS.turquoise }}>{todayOrders.length}</div>
            <div className="text-gray-700 font-medium text-body-lg">order{todayOrders.length !== 1 ? 's' : ''} today</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setSelectedTab('active')}
            className={`flex-1 py-3 px-3 rounded-xl font-semibold transition-all shadow-sm text-body-sm ${
              selectedTab === 'active'
                ? 'text-midnight shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            style={selectedTab === 'active' ? { backgroundColor: BRAND_COLORS.teal } : {}}
          >
            {t.today} ({todayOrders.length})
          </button>
          <button
            onClick={() => setSelectedTab('completed')}
            className={`flex-1 py-3 px-3 rounded-xl font-semibold transition-all shadow-sm text-body-sm ${
              selectedTab === 'completed'
                ? 'text-midnight shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            style={selectedTab === 'completed' ? { backgroundColor: BRAND_COLORS.teal } : {}}
          >
            {t.previous} ({previousOrders.length})
          </button>
          <button
            onClick={() => setSelectedTab('walkin')}
            className={`flex-1 py-3 px-3 rounded-xl font-semibold transition-all shadow-sm text-body-sm ${
              selectedTab === 'walkin'
                ? 'text-midnight shadow-md'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            style={selectedTab === 'walkin' ? { backgroundColor: BRAND_COLORS.teal } : {}}
          >
            {t.walkInOrder}
          </button>
        </div>

        {/* Orders List */}
        <div>
          {loading ? (
            <div className="space-y-4">
              {/* Loading Skeleton - Order Cards */}
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl shadow-md p-5 animate-pulse">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="h-6 bg-gray-200 rounded w-1/3 mb-3"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                    <div className="w-5 h-5 bg-gray-200 rounded"></div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="h-5 bg-gray-200 rounded w-20"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div key={selectedTab} className={`view-anim-${tabDirection}`}>
              {selectedTab === 'active' && (
                <>
                  {todayOrders.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md">
                      <EmptyState
                        illustration={<NoActiveOrdersIllustration />}
                        title={t.emptyTodayTitle}
                        message={t.emptyTodayMessage}
                        actionButton={
                          <button
                            onClick={playDing}
                            className="text-midnight px-6 h-touch-md rounded-btn font-semibold text-body-lg transition-all active:scale-[0.98] hover:opacity-90 inline-flex items-center justify-center gap-2"
                            style={{ backgroundColor: BRAND_COLORS.teal }}
                          >
                            <Bell size={18} />
                            {t.testNotificationSound}
                          </button>
                        }
                      />
                    </div>
                  ) : (
                    todayOrders.map(order => OrderCard({ order }))
                  )}
                </>
              )}

              {selectedTab === 'completed' && (
                <>
                  {previousOrders.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md">
                      <EmptyState
                        illustration={<NoActiveOrdersIllustration />}
                        title={t.emptyPreviousTitle}
                        message={t.emptyPreviousMessage}
                        actionButton={
                          <button
                            onClick={() => setSelectedTab('active')}
                            className="text-midnight px-6 h-touch-md rounded-btn font-semibold text-body-lg transition-all active:scale-[0.98] hover:opacity-90 inline-flex items-center justify-center"
                            style={{ backgroundColor: BRAND_COLORS.teal }}
                          >
                            {t.today}
                          </button>
                        }
                      />
                    </div>
                  ) : (
                    previousOrders.map(order => OrderCard({ order }))
                  )}
                </>
              )}

              {selectedTab === 'walkin' && (
                <div className="bg-white rounded-xl shadow-md p-6">
                  {/* Step 1: Family Code Entry */}
                  {walkInView === 'code-entry' && (
                    <>
                      <h3 className="text-title-sm font-bold text-gray-900 mb-4">{t.walkInTitle}</h3>
                      <p className="text-gray-600 mb-6">{t.verifyIdentityIntro}</p>

                      <div className="mb-6">
                        <label className="block text-body-sm font-semibold text-gray-700 mb-2">
                          {t.walkInStep1}
                        </label>
                        <p className="text-body-sm text-gray-500 mb-3">
                          {t.walkInStep1Hint}
                        </p>
                        <input
                          type="text"
                          value={familyCodeInput}
                          onChange={(e) => {
                            setFamilyCodeInput(e.target.value.toUpperCase());
                            setVerificationError('');
                          }}
                          onKeyPress={(e) => e.key === 'Enter' && verifyFamilyCode()}
                          placeholder={t.walkInStep1Placeholder}
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="characters"
                          spellCheck="false"
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 transition-all text-body-lg font-mono"
                          style={{
                            borderColor: verificationError ? '#DC2626' : '#d1d5db',
                            focusRingColor: BRAND_COLORS.teal
                          }}
                          autoFocus
                        />
                        {verificationError && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-body-sm text-red-700 font-medium">{verificationError}</p>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={verifyFamilyCode}
                        disabled={!familyCodeInput.trim() || verifyingCode}
                        className="w-full h-touch-md rounded-btn font-semibold text-body-lg text-midnight transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: BRAND_COLORS.teal }}
                      >
                        {verifyingCode ? (
                          <span className="flex items-center justify-center gap-2">
                            <RefreshCw className="animate-spin" size={20} />
                            {t.verifyingShort}
                          </span>
                        ) : (
                          t.verifyFamilyCodeBtn
                        )}
                      </button>

                      {demoMode && (
                        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-body-sm text-blue-800">
                            <strong>Demo codes:</strong> SmiS1234, CheC5678, GarG9012 (spaces optional)
                          </p>
                        </div>
                      )}

                      <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-body-sm font-semibold text-gray-800 mb-1">{t.needHelp}</p>
                        <p className="text-body-sm text-gray-700">
                          {t.walkInCallCenter}{' '}
                          <a href="tel:+14154801185" className="font-bold hover:underline" style={{ color: BRAND_COLORS.turquoise }}>
                            (415) 480-1185
                          </a>
                          {' '}{t.walkInCallCenterSuffix}
                        </p>
                      </div>
                    </>
                  )}

                  {/* Step 2: Phone Verification */}
                  {walkInView === 'phone-verification' && selectedFamily && (
                    <>
                      <h3 className="text-title-sm font-bold text-gray-900 mb-4">{t.walkInTitle}</h3>
                      <p className="text-gray-600 mb-6">{t.familyCodeVerifiedIntro}</p>

                      <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 text-green-700 font-semibold mb-1">
                          <Check size={20} />
                          {t.familyCodeVerifiedBadge} {selectedFamily.familyCode}
                        </div>
                      </div>

                      <div className="mb-6">
                        <label className="block text-body-sm font-semibold text-gray-700 mb-2">
                          {t.walkInStep2}
                        </label>
                        <p className="text-body-sm text-gray-500 mb-3">
                          {t.walkInStep2Hint}
                        </p>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={phoneInput}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 4) {
                              setPhoneInput(value);
                              setVerificationError('');
                            }
                          }}
                          onKeyPress={(e) => e.key === 'Enter' && phoneInput.length === 4 && verifyPhone()}
                          placeholder={t.walkInLast4Placeholder}
                          maxLength={4}
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="off"
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 transition-all text-body-lg font-mono text-center"
                          style={{
                            borderColor: verificationError ? '#DC2626' : '#d1d5db',
                            focusRingColor: BRAND_COLORS.teal
                          }}
                          autoFocus
                        />
                        {verificationError && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-body-sm text-red-700 font-medium">{verificationError}</p>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={verifyPhone}
                        disabled={phoneInput.length !== 4 || verifyingPhone}
                        className="w-full h-touch-md rounded-btn font-semibold text-body-lg text-midnight transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed mb-3"
                        style={{ backgroundColor: BRAND_COLORS.teal }}
                      >
                        {verifyingPhone ? (
                          <span className="flex items-center justify-center gap-2">
                            <RefreshCw className="animate-spin" size={20} />
                            {t.verifyingShort}
                          </span>
                        ) : (
                          t.verifyPhoneBtn
                        )}
                      </button>

                      <button
                        onClick={resetWalkInOrder}
                        className="w-full h-touch-md rounded-btn font-semibold text-body-lg text-gray-700 bg-gray-100 transition-all active:scale-[0.98] hover:bg-gray-200"
                      >
                        Start Over
                      </button>

                      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-body-sm text-blue-800">
                          <strong>Demo hint:</strong> Last 4 of {selectedFamily.familyCode}'s phones: {selectedFamily.phone.replace(/\D/g, '').slice(-4)}
                          {selectedFamily.secondaryPhone && ` or ${selectedFamily.secondaryPhone.replace(/\D/g, '').slice(-4)}`}
                        </p>
                      </div>
                    </>
                  )}

                  {/* Step 3: Family Verified - Place Order */}
                  {walkInView === 'family-verified' && selectedFamily && (
                    <>
                      <div className="flex items-center justify-between mb-6">
                        <button
                          onClick={resetWalkInOrder}
                          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
                        >
                          <ArrowLeft size={20} />
                          <span className="font-medium">{t.startOver || 'Start Over'}</span>
                        </button>
                        {walkInCart.length > 0 && (
                          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100">
                            <ShoppingCart size={18} style={{ color: BRAND_COLORS.turquoise }} />
                            <span className="font-semibold">{walkInCart.length} item{walkInCart.length > 1 ? 's' : ''}</span>
                          </div>
                        )}
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <div className="text-body-sm font-semibold text-gray-500 uppercase">{t.familyCodeLabel}</div>
                            <div className="font-bold text-gray-900 text-title-sm" dir="ltr">{selectedFamily.familyCode}</div>
                            <div className="text-body-sm text-gray-600 mt-2">
                              <strong>{t.languageLabel}:</strong> {selectedFamily.language}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-label font-semibold text-gray-500 uppercase">{t.mealsLabel}</div>
                            <div
                              className="text-title-lg font-bold"
                              style={{
                                color: (selectedFamily.vouchersRemaining - walkInCart.reduce((sum, item) => sum + item.vouchers, 0)) === 0
                                  ? '#DC2626' // Red when 0
                                  : BRAND_COLORS.teal
                              }}
                            >
                              {selectedFamily.vouchersRemaining - walkInCart.reduce((sum, item) => sum + item.vouchers, 0)}
                            </div>
                            <div className="text-label text-gray-500">
                              of {selectedFamily.vouchersPerCycle || 8} remaining
                            </div>
                          </div>
                        </div>

                        {/* Dietary Restrictions */}
                        {(() => {
                          const filtered = filterDietaryByLanguage(selectedFamily.dietaryRestrictions, language);
                          if (!filtered.length) return null;
                          return (
                          <div className="border-t border-gray-300 pt-3">
                            <div className="text-label font-bold text-gray-500 uppercase mb-2">{t.dietaryRestrictionsLabel}</div>
                            <div className="flex flex-wrap gap-2">
                              {filtered.map((restriction, idx) => (
                                <span
                                  key={idx}
                                  className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-body-sm font-medium"
                                >
                                  {restriction}
                                </span>
                              ))}
                            </div>
                          </div>
                          );
                        })()}
                      </div>

                      {/* Warning when no vouchers remaining */}
                      {(selectedFamily.vouchersRemaining - walkInCart.reduce((sum, item) => sum + item.vouchers, 0)) === 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                              <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div>
                              <h4 className="font-bold text-red-900 mb-1">No Meals Remaining</h4>
                              <p className="text-body-sm text-red-800">
                                This family has used all their available meals. To change the order, remove an item from the cart below.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <h3 className="text-title-sm font-bold text-gray-900 mb-4">{t.selectMenuItemsTitle}</h3>

                      <div className="space-y-4 mb-6">
                        {restaurant.menu && restaurant.menu.length > 0 && restaurant.menu.map(item => {
                          const vouchersUsed = walkInCart.reduce((sum, cartItem) => sum + cartItem.vouchers, 0);
                          const canAdd = vouchersUsed + item.vouchers <= selectedFamily.vouchersRemaining;

                          return (
                            <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
                              <img
                                src={item.imageUrl}
                                alt={item.nameEN}
                                className="w-full h-40 object-cover"
                                onError={(e) => {
                                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='160'%3E%3Crect fill='%23f0f0f0' width='400' height='160'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999' font-size='14'%3EImage not available%3C/text%3E%3C/svg%3E";
                                }}
                              />
                              <div className="p-4">
                                <h4 className="font-bold text-gray-900 mb-2">{item.nameEN}</h4>
                                {Array.isArray(item.dietaryConsiderations) && item.dietaryConsiderations.length > 0 && (
                                  <div className="mb-2 flex flex-wrap gap-1.5">
                                    {item.dietaryConsiderations.map((label, idx) => (
                                      <span
                                        key={idx}
                                        className="inline-flex items-center bg-gray-100 text-gray-700 text-label px-2 py-0.5 rounded-full font-medium"
                                      >
                                        {label}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <p className="text-body-sm text-gray-600 mb-3">{item.descriptionEN}</p>
                                <div className="flex justify-between items-center gap-2">
                                  <div className="inline-flex flex-shrink-0 items-center text-midnight text-label px-3 py-1.5 rounded-full font-semibold" style={{ backgroundColor: BRAND_COLORS.teal }}>
                                    <span>{item.vouchers} meal{item.vouchers > 1 ? 's' : ''}</span>
                                  </div>
                                  <div className="relative group flex-shrink min-w-0">
                                    <button
                                      onClick={() => addToWalkInCart(item)}
                                      disabled={!canAdd}
                                      className={`px-3 py-1.5 text-body-sm rounded-lg font-semibold transition-all ${
                                        canAdd
                                          ? 'text-midnight hover:opacity-90'
                                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                      }`}
                                      style={canAdd ? { backgroundColor: BRAND_COLORS.teal } : {}}
                                    >
                                      {canAdd ? t.addToOrderBtn : t.notEnoughMealsBtn}
                                    </button>
                                    {!canAdd && (
                                      <div className="absolute bottom-full end-0 mb-2 hidden group-hover:block z-10">
                                        <div className="bg-gray-900 text-white text-label rounded-lg py-2 px-3 whitespace-nowrap">
                                          Insufficient meals remaining
                                          <div className="absolute top-full end-4 -mt-1">
                                            <div className="border-4 border-transparent border-t-gray-900"></div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Spacer so the last menu item can scroll above the sticky order
                          summary. The summary grows tall when the item list is expanded,
                          so the spacer matches: ~larger when expanded. Plus safe-area. */}
                      {walkInCart.length > 0 && (
                        <div className={walkInCartExpanded
                          ? 'h-[28rem] [padding-bottom:env(safe-area-inset-bottom)]'
                          : 'h-44 [padding-bottom:env(safe-area-inset-bottom)]'}></div>
                      )}

                      {/* Sticky Order Summary at Bottom */}
                      {walkInCart.length > 0 && (
                        <div
                          className={`fixed bottom-0 start-0 end-0 bg-white border-t-2 shadow-2xl transition-all duration-300 ${
                            justAddedItem ? 'animate-pulse-glow' : ''
                          }`}
                          style={{
                            borderTopColor: BRAND_COLORS.teal,
                            paddingBottom: 'env(safe-area-inset-bottom)',
                            zIndex: 1000
                          }}
                        >
                          <div className="max-w-7xl mx-auto px-4 py-4">
                            {/* Compact summary header */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: BRAND_COLORS.teal + '20' }}>
                                  <ShoppingCart size={18} style={{ color: BRAND_COLORS.turquoise }} />
                                  <span className="font-bold" style={{ color: BRAND_COLORS.turquoise }}>
                                    {walkInCart.length} item{walkInCart.length > 1 ? 's' : ''}
                                  </span>
                                </div>
                                <span className="text-body-sm text-gray-600">
                                  {walkInCart.reduce((sum, item) => sum + item.vouchers, 0)} meal{walkInCart.reduce((sum, item) => sum + item.vouchers, 0) > 1 ? 's' : ''}
                                </span>
                              </div>
                              <button
                                onClick={submitWalkInOrder}
                                disabled={submittingWalkIn}
                                className="text-midnight px-6 h-touch-md rounded-btn font-semibold text-body-lg shadow-elevation-2 transition-all active:scale-[0.98] hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
                                style={{ backgroundColor: BRAND_COLORS.teal }}
                              >
                                {t.placeOrderBtn}
                              </button>
                            </div>

                            {/* Error from a failed POST — without this, walk-in
                                submissions that fail server-side would look like
                                "nothing happens" because the existing verificationError
                                slots only render at code-entry / phone-verification. */}
                            {verificationError && (
                              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-body-sm text-red-700 font-medium">{verificationError}</p>
                              </div>
                            )}

                            {/* Expandable item list */}
                            <details
                              className="group"
                              open={walkInCartExpanded}
                              onToggle={(e) => setWalkInCartExpanded(e.currentTarget.open)}
                            >
                              <summary
                                className="cursor-pointer text-body-sm font-bold flex items-center gap-2 py-2 px-3 rounded-lg hover:opacity-80 transition-all list-none"
                                style={{ color: BRAND_COLORS.turquoise, backgroundColor: BRAND_COLORS.teal + '15' }}
                              >
                                <span>Review / Remove Items ({walkInCart.length})</span>
                                <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </summary>
                              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                                {walkInCart.map((item, index) => (
                                  <div key={index} className="flex justify-between items-center bg-gray-50 rounded-lg p-3">
                                    <div>
                                      <div className="font-semibold text-gray-900 text-body-sm">{item.nameEN}</div>
                                      <div className="text-label text-gray-600">{item.vouchers} meal{item.vouchers > 1 ? 's' : ''}</div>
                                    </div>
                                    <button
                                      onClick={() => removeFromWalkInCart(index)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <X size={18} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </details>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {walkInView === 'order-placed' && (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">✅</div>
                      <h3 className="text-title-md font-bold text-gray-900 mb-2">Order Placed Successfully!</h3>
                      <p className="text-gray-600 mb-6">
                        The order has been added to your active orders.
                      </p>
                      <p className="text-body-sm font-semibold" style={{ color: BRAND_COLORS.turquoise }}>
                        Redirecting to Active Orders...
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Alpha testing feedback button */}
      <button
        onClick={() => window.open('https://form.asana.com/?k=RiHhVqb0NLaAJgKaDMPhHA&d=1199705537708056', '_blank')}
        className="fixed bottom-20 end-4 z-50 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-midnight text-body-sm font-medium"
        style={{ backgroundColor: BRAND_COLORS.teal }}
        aria-label="Report a bug"
      >
        <MessageSquare size={18} />
        Report Bug
      </button>

      <ConfirmModal {...confirmProps} />
    </div>
  );
}